// src/modules/app-views/companies/pages/Document/tabs/RecordTab/RegisterCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card } from 'antd';
import translateX from 'utils/translateX';
import SimpleEntityForm from 'components/SimpleComponents/SimpleEntityForm/SimpleEntityForm';
import { useDocumentContext } from 'components/core/DocumentProvider';
import appService from 'services/appService';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RegisterCard = ({ setState, state = {}, entityTypeModel, isHybridMode = false }) => {
  // ---------------------------------------------------------------------------
  // PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { data } = useDocumentContext();
  const storedParameters = useZustand('app', 'parameters');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedUser = useZustand('auth', 'user');

  // ---------------------------------------------------------------------------
  // HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  // Get entityProfile from module config (config.js) dynamically
  // This allows the component to work correctly across different modules
  // (companies, customers, suppliers, collaborators)
  const moduleEntityProfile = useMemo(() => {
    // Try pagination.filter first (list pages)
    if (storedCurrentPage?.pagination?.filter?.entityProfile) {
      return storedCurrentPage.pagination.filter.entityProfile;
    }
    // Try api.query.filter (document pages - JSON string)
    if (storedCurrentPage?.api?.query?.filter) {
      try {
        const parsed = JSON.parse(storedCurrentPage.api.query.filter);
        if (parsed?.entityProfile) return parsed.entityProfile;
      } catch {
        // ignore parse errors
      }
    }
    // Fallback to 'customer' as last resort (most common entity type)
    return ['customer'];
  }, [storedCurrentPage?.pagination?.filter?.entityProfile, storedCurrentPage?.api?.query?.filter]);

  // Default state values if not provided (prevents undefined errors during initial render)
  // NOTE: entityType must be 'company' or 'person' (NOT 'customer' - that's entityProfile)
  const safeState = {
    dataOrigin: state.dataOrigin || 'new_data',
    entityType: state.entityType || 'company',
    entityProfile: state.entityProfile || moduleEntityProfile,
    ...state,
  };

  // Get internationalization preference from module settings
  // Re-reads when storedParameters changes (e.g., after saving in parameters modal)
  // For customers module, always show country selector (dynamic forms)
  // For companies module, use allowForeignCountries preference
  const allowForeignCountries = useMemo(() => {
    const savedPrefs = appService.getModulePreferences({ category: 'form' });
    const isCustomerModule = Array.isArray(moduleEntityProfile) && moduleEntityProfile.includes('customer');
    return isCustomerModule ? true : (savedPrefs?.allowForeignCountries ?? false);
  }, [storedParameters, moduleEntityProfile]);

  // Determine card title based on entityType and mode
  const getCardTitle = () => {
    // if (isHybridMode) {
    // In hybrid mode, show dynamic title based on current detected type
    // return safeState.entityType === 'person' ? 'person_data' : 'company_data';
    // return safeState.entityType === 'person' ? 'individual_person' : 'legal_person';
    // }
    // return safeState.entityType === 'person' ? 'person_data' : 'company_data';
    return safeState.entityType === 'person' ? 'individual_person' : 'legal_person';
  };
  const cardTitle = getCardTitle();

  // Determine if we should search by API
  const isCompany = safeState.entityType === 'company';

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <Card title={<div className="text-muted">{translateX(cardTitle)}</div>}>
      <SimpleEntityForm
        setState={setState}
        state={safeState}
        // entityProfile default - derived from module config (e.g., ['operator'], ['customer'])
        defaultEntityProfiles={safeState.entityProfile}
        // selectors visibility
        // - selectable: show the entire SimpleDataOriginForm area
        // - hideDataOriginSelector: hide the existing_data/new_data radio buttons
        // - hideEntityTypeSelector: hide the company/person segment selector
        selectable={true}
        hideDataOriginSelector={true} // always hide data origin selector
        hideEntityTypeSelector={!isHybridMode} // show entity type selector only in hybrid mode
        hideEntityProfileSelector={true} // always hide entity profile selector
        dataOrigin={safeState.dataOrigin}
        showAdditionalInfoForm={true}
        showAddressForm={true}
        showEntityTypeLabel={false}
        showCountryLabel={false}
        // entity setting
        // entityTypeModel controls which form is shown:
        // - 'company' = only company form (CNPJ)
        // - 'person' = only person form (CPF)
        // - 'manual' = user can select (hybrid mode)
        entityTypeModel={entityTypeModel}
        entityType={safeState.entityType}
        // unique validation (check if entity already exists by document)
        unique={true}
        // search external API for auto-fill
        searchByApi={true} // hybrid mode uses searchByApi
        // searchCnpjByApi={!isHybridMode && isCompany} // company-only mode
        searchCnpjByApi={true} // company-only mode
        // searchCpfByApi={false} // no CPF API yet
        // country selectors
        // Show country selector based on internationalization setting from module preferences
        // allowForeignCountries: true = show country selector, false = hide it
        hideFormCountrySelector={!allowForeignCountries}
        hideAddressCountrySelector={true}
        // phone setting
        companyPhoneProfile={null}
        isEnrichedAndPersisted={data?.dataEnrichment?.isEnriched}
        // country precedence: subscription → user → data → locale
        subscriptionCountryCode={storedSubscription?.countryCode}
        userCountryCode={storedUser?.countryCode}
      />
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RegisterCard;

// *******************************************************************************
// HELPERS
// *******************************************************************************
